import axios, { AxiosError, type AxiosResponse } from "axios";

export const baseAxiosInstance = axios.create();
export const axiosInstance = axios.create({ timeout: 30000 });

export const useGetRequest = axiosInstance.get.bind(axiosInstance);
export const usePostRequest = axiosInstance.post.bind(axiosInstance);
export const usePatchRequest = axiosInstance.patch.bind(axiosInstance);
export const useDeleteRequest = axiosInstance.delete.bind(axiosInstance);
export const usePutRequest = axiosInstance.put.bind(axiosInstance);

async function errorHelper(error: AxiosError): Promise<AxiosError> {
  // Extract custom error message from server response if available
  const customMessage = (error?.response?.data as any)?.data?.message;
  if (customMessage) {
    return Promise.reject(new Error(customMessage));
  }

  // Build user-friendly error message based on the error type
  let userMessage = error.message;
  const requestUrl = error.config?.url || "unknown";
  const requestMethod = error.config?.method?.toUpperCase() || "REQUEST";

  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    // Timeout error - user-friendly message
    userMessage = "The request took too long to complete. Please try again.";
  } else if (error.request && !error.response) {
    // Network error: request was made but no response received
    userMessage =
      "Unable to connect to the server. Please check your internet connection and try again.";
  } else if (error.response) {
    // Server responded with an error status
    const status = error.response.status;
    if (status >= 500) {
      userMessage = "A server error occurred. Please try again later.";
    } else if (status === 404) {
      userMessage = "The requested resource was not found.";
    } else if (status === 403) {
      userMessage = "You don't have permission to access this resource.";
    } else {
      userMessage = "An error occurred while processing your request.";
    }
  }

  // Create error with user-friendly message
  const userError = new Error(userMessage);

  // Add technical details as custom properties for debugging/logging (e.g., Bugsnag)
  // These won't be shown to users but will be captured by error monitoring
  (userError as any).requestUrl = requestUrl;
  (userError as any).requestMethod = requestMethod;
  (userError as any).errorCode = error.code;
  (userError as any).statusCode = error.response?.status;
  (userError as any).originalMessage = error.message;

  return Promise.reject(userError);
}

function responseHelper<
  T extends Record<string, any>,
  A extends AxiosResponse<any> = AxiosResponse<T>,
>(response: A): A {
  if (response?.data?.response) {
    response.data = response.data.response;
  } else if (response?.data?.data) {
    response.data = response.data.data;
  }
  return response;
}

function pickAPI(url: string | undefined, session: typeof currentSessionInformation) {
  if (url && url.indexOf("/account") > -1) {
    return session.appBillingApiUrl;
  }
  return session.appBackendApiUrl;
}

axiosInstance.interceptors.request.use(config => {
  const session = currentSessionInformation;
  config.headers["Authorization"] =
    config.headers["Authorization"] || `Bearer ${session?.appAuthToken}`;
  config.headers["Content-Type"] = config.headers["Content-Type"] || "application/json";
  config.headers["Accept-Language"] =
    config.headers["Accept-Language"] || session.preferredLanguage;
  config.baseURL = config.baseURL || pickAPI(config.url, session);
  if (import.meta.env.MODE !== "test") {
    config.headers["X-Marketing-Assistant-App-Version"] = `web/${session.appVersion}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  r => responseHelper(r),
  e => errorHelper(e)
);
